/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import Text from 'src/components/ts-components/Text'
import ProfilePlayerStatsRow from 'src/features/profile/components/ProfilePlayerStatsRow'
import { ProfilePlayer } from 'src/features/profile/types'

interface RegistrationEntityCardProps {
  onClick?: () => void
  disabled?: boolean
  name: string
  image?: string
  selected?: boolean
  playerId?: string
  /** When provided, renders the age/weight/height stat chips (same as the
   *  profile player-switch modal) beneath the name. */
  player?: ProfilePlayer | null
  /**
   * `tile` is the original fixed 200x200 card with a solid PRIMARY fill when
   * selected. `grid` stretches to its cell and borrows the division/team card
   * treatment — transparent ground, PRIMARY border when chosen, the rest dimmed
   * — so a grid of people reads the same way as a grid of divisions.
   */
  variant?: 'tile' | 'grid'
  /** `grid` only: dims this card while a different one is selected. */
  hasSelection?: boolean
}

const RegistrationEntityCard = ({
  onClick,
  disabled = false,
  name,
  image = '/images/generic_placeholders/placeholder_player.png',
  selected = false,
  playerId,
  player,
  variant = 'tile',
  hasSelection = false,
}: RegistrationEntityCardProps) => {
  const isGrid = variant === 'grid'
  // Only the tile variant fills with PRIMARY, so only it needs the on-primary
  // content color; the grid variant sits on the page ground throughout.
  const contentColor =
    selected && !isGrid ? PRIMARY_BUTTON_CONTENT.text : colors.NEUTRAL_0

  return (
    <div
      css={
        isGrid
          ? css`
              background-color: ${selected
                ? colors.NEUTRAL_700
                : 'transparent'};
              border: ${selected
                ? `2px solid ${colors.PRIMARY}`
                : `1px solid ${colors.NEUTRAL_500}`};
              border-radius: 16px;
              padding: 16px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              width: 100%;
              height: 100%;
              min-height: 180px;
              cursor: ${disabled ? 'not-allowed' : 'pointer'};
              opacity: ${disabled ? 0.5 : hasSelection && !selected ? 0.5 : 1};
              transition: border-color 0.15s ease-in-out,
                opacity 0.15s ease-in-out;

              &:hover {
                border-color: ${disabled ? undefined : colors.PRIMARY};
              }
            `
          : css`
              background-color: ${selected
                ? colors.PRIMARY
                : colors.NEUTRAL_600};
              border: ${selected ? 'none' : `1px solid ${colors.NEUTRAL_100}`};
              border-radius: 16px;
              padding: 16px;
              display: flex;
              flex-direction: column;
              align-items: center;
              cursor: ${disabled ? 'not-allowed' : 'pointer'};
              opacity: ${disabled ? 0.5 : 1};
              transition: all 0.2s ease-in-out;
              height: 200px;
              width: 200px;

              &:hover {
                transform: ${disabled ? 'none' : 'translateY(-4px)'};
                box-shadow: ${disabled
                  ? 'none'
                  : '0 4px 12px rgba(0, 0, 0, 0.2)'};
              }

              @media (max-width: 768px) {
                min-width: 150px;
                padding: 16px;
              }
            `
      }
      onClick={disabled ? undefined : onClick}
    >
      <div
        css={css`
          background-color: ${colors.NEUTRAL_800};
          border-radius: 50%;
          width: 72px;
          height: 72px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
          overflow: hidden;

          @media (max-width: 768px) {
            width: 64px;
            height: 64px;
          }
        `}
      >
        <img
          src={image}
          alt={name}
          css={css`
            width: 100%;
            height: 100%;
            object-fit: cover;
          `}
        />
      </div>

      <Text
        variant='heading6'
        color={contentColor}
        style={`
          text-align: center;
          margin-bottom: 8px;
          word-break: break-word;
        `}
      >
        {name}
      </Text>
      {playerId !== 'new' && player ? (
        <ProfilePlayerStatsRow
          player={player}
          compact
          /* Recolor the stats to match the name on the selected (PRIMARY) card. */
          color={selected && !isGrid ? PRIMARY_BUTTON_CONTENT.text : undefined}
        />
      ) : null}
    </div>
  )
}

export default RegistrationEntityCard
